import React, { useState } from "react";
import { 
  Search, MapPin, Sparkles, ShieldCheck, CheckCircle2, ArrowRight, 
  Building2, Users, Briefcase, TrendingUp, HelpCircle, ChevronRight, 
  ExternalLink, Compass, Check, X, Phone, Mail, Clock, RefreshCw,
  Star, Award, Layers, BarChart3, Target, DollarSign, Eye, AlertCircle, Info
} from "lucide-react";
import { AudienceModeId, AUDIENCE_MODES } from "../config/audienceModes";
import { getCategorySlugFromText } from "../utils/jobCategoryRegistry";
import { resolveLocationSlug } from "../utils/locationRoutes";
import { resolveCanonicalSearchIntent } from "../utils/canonicalIntentResolver";

interface HomePageRebuildProps {
  onNavigate: (path: string) => void;
  onLogin: (openSignUp?: boolean) => void;
  onHomepageSearch?: (query: string, location: string) => void;
}

export default function HomePageRebuild({ onNavigate, onLogin, onHomepageSearch }: HomePageRebuildProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [activeAudienceMode, setActiveAudienceMode] = useState<AudienceModeId>("job_seeker");
  const [demoSignalTab, setDemoSignalTab] = useState<"high" | "possible" | "unconfirmed" | "improbable" | "low">("high");
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const resolved = resolveCanonicalSearchIntent(searchQuery, locationQuery);
    if (resolved && resolved.isStructuredIntent) {
      onNavigate(resolved.canonicalPath);
      return;
    }
    if (onHomepageSearch) {
      onHomepageSearch(searchQuery, locationQuery);
    } else {
      onNavigate(`/find-jobs?q=${encodeURIComponent(searchQuery)}&loc=${encodeURIComponent(locationQuery)}`);
    }
  };

  const demoSignalData = {
    high: {
      badge: "High Signal",
      color: "bg-emerald-50 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/80",
      reason: "Direct active role found on official domain: 'Front Desk Receptionist - Posted 3 days ago'.",
      freshness: "Checked 2 hours ago",
      verifiability: "100% Verifiable"
    },
    possible: {
      badge: "Possible Signal",
      color: "bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/80",
      reason: "Official careers page found at /careers with active general talent intake form.",
      freshness: "Checked 5 hours ago",
      verifiability: "High (Domain Verified)"
    },
    unconfirmed: {
      badge: "Unconfirmed Signal",
      color: "bg-amber-50 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800/80",
      reason: "Commercial growth surge: 34 new 5-star Google reviews in 30 days & expanded office hours.",
      freshness: "Checked 12 hours ago",
      verifiability: "Medium (Growth Verified)"
    },
    improbable: {
      badge: "Improbable Signal",
      color: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700",
      reason: "Static single-location website with zero career page or recent web updates.",
      freshness: "Checked 1 day ago",
      verifiability: "Low"
    },
    low: {
      badge: "Low Signal",
      color: "bg-rose-50 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/80",
      reason: "No website found or domain unreachable.",
      freshness: "Checked 2 days ago",
      verifiability: "Unverified Footprint"
    }
  };

  const currentDemoSignal = demoSignalData[demoSignalTab];

  const audienceCards = [
    {
      id: "job_seeker",
      title: "Job Seekers & Career Changers",
      painPoint: "Stuck in high-volume queues on national job boards with little visibility.",
      benefit: "Discover local businesses directly and organize personalized outreach before positions are heavily advertised.",
      exampleSignal: "Dental practice added secondary facility location & active intake form.",
      link: "/for-job-seekers"
    },
    {
      id: "recruiter",
      title: "Recruiters & Search Firms",
      painPoint: "Cold-calling reluctant client accounts with no prior indicator of staffing demand.",
      benefit: "Identify regional employers experiencing review surges and expansion signals to pitch placement services.",
      exampleSignal: "Trade contractor experiencing 40% review velocity spike.",
      link: "/for-recruiters"
    },
    {
      id: "staffing_agency",
      title: "Staffing Agencies",
      painPoint: "Missing seasonal shifts and multi-location staffing demand spikes.",
      benefit: "Source high-volume commercial and temporary staffing leads across healthcare, trades, and retail.",
      exampleSignal: "Regional clinic network opening new weekend operating shifts.",
      link: "/for-staffing-agencies"
    },
    {
      id: "career_coach",
      title: "Career Coaches",
      painPoint: "Clients struggling to find local opportunities that match their specific commute and skills.",
      benefit: "Curate client-specific employer target lists and track outreach progress in shared workspaces.",
      exampleSignal: "Local technology agency adding junior designer intake role.",
      link: "/for-career-coaches"
    },
    {
      id: "workforce_org",
      title: "Workforce Organizations",
      painPoint: "Lack of granular local business data for community employment programs.",
      benefit: "Map regional employer demand indicators and support local job seekers with structured CRM workflows.",
      exampleSignal: "Community medical group expanding outpatient care staff.",
      link: "/for-workforce-organizations"
    },
    {
      id: "college_career",
      title: "College Career Centers",
      painPoint: "Students struggling to locate entry-level roles and internships at nearby small businesses.",
      benefit: "Connect students with nearby medical practices, law firms, and trade shops seeking local talent.",
      exampleSignal: "Local accounting firm looking for student tax season assistants.",
      link: "/for-college-career-centers"
    },
    {
      id: "sales_pro",
      title: "Sales Professionals & BD",
      painPoint: "Spending hours manually building local business lists with missing contact details.",
      benefit: "Discover qualified B2B sales prospects with verified phone lines, websites, and decision-maker contact points.",
      exampleSignal: "Multi-location auto group updating digital web presence.",
      link: "/for-sales-teams"
    },
    {
      id: "agency",
      title: "Agencies & Freelancers",
      painPoint: "Pitching generic web/SEO proposals to businesses with no visible need.",
      benefit: "Scan nearby small businesses for missing websites, slow mobile loading, or broken contact forms to win web design clients.",
      exampleSignal: "High-rated law practice running an outdated non-mobile website.",
      link: "/for-agencies"
    },
    {
      id: "business_explorer",
      title: "Local Business Explorers",
      painPoint: "Hard to discover independent local businesses in specific commuting neighborhoods.",
      benefit: "Explore neighborhood commercial ecosystems, map company footprints, and evaluate local business health.",
      exampleSignal: "Boutique coffee chain opening 3rd local storefront.",
      link: "/business-explorer"
    },
    {
      id: "business_dev",
      title: "Business Development Teams",
      painPoint: "Identifying strategic partnership targets in specific geographic markets.",
      benefit: "Map growing regional companies and evaluate market expansion opportunities with clean data.",
      exampleSignal: "Regional logistics provider expanding warehouse capacity.",
      link: "/for-business-development"
    }
  ];

  const faqList = [
    {
      q: "What is BizNearYou and how is it different from a job board?",
      a: "BizNearYou is a local business discovery and signal intelligence platform—not a passive job board. Instead of listing static job ads, BizNearYou tracks public growth indicators, careers pages, and local commercial activity so you can contact business owners directly before job postings hit major job portals."
    },
    {
      q: "Are the job signals guaranteed?",
      a: "No. BizNearYou maintains a strict conservative standard and never guarantees employment. We show you real, public evidence (like active careers page URLs, review surges, or facility expansions) along with freshness timestamps so you can verify the opportunity directly before reaching out."
    },
    {
      q: "How does BizNearYou protect user and API costs?",
      a: "We enforce strict caching, re-use existing public business records, and never trigger paid API lookups unnecessarily. Free users receive generous daily search limits, while Pro and Team accounts unlock unlimited CRM leads and deep enrichment."
    },
    {
      q: "Is BizNearYou free to use?",
      a: "Yes! Job seekers and local explorers can search local businesses, inspect hiring signals, and manage saved employers completely free. Pro plans are available for recruiters, sales teams, and agencies needing high-volume CRM export tools."
    },
    {
      q: "How do I contact a business listed on BizNearYou?",
      a: "Every result card provides verified public contact routes—including phone numbers, official website links, and career intake forms. We also provide structured outreach tracking tools to help you organize polite email inquiries or telephone calls."
    }
  ];

  return (
    <div className="bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 min-h-screen font-sans">
      
      {/* Schema Injection */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "BizNearYou",
          "url": "https://getbiznearyou.com/",
          "description": "Find local business opportunities, hiring signals, and unadvertised job openings before they hit major job boards.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://getbiznearyou.com/find-jobs?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqList.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": f.a
            }
          }))
        })}
      </script>

      {/* 1. HERO SECTION */}
      <section className="relative bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 pt-8 sm:pt-12 pb-14 sm:pb-20 px-4 sm:px-6 lg:px-8 text-center overflow-hidden">
        
        {/* Decorative Grid Accent */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

        <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 relative z-10 w-full">
          
          {/* Eyebrow Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-[11px] sm:text-xs font-black uppercase tracking-wider shadow-xs max-w-full">
            <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
            <span className="truncate sm:whitespace-normal">Uncover Unadvertised Local Opportunities</span>
          </div>

          {/* Main H1 Title */}
          <h1 className="text-2xl min-[375px]:text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.15] sm:leading-[1.12]">
            Find local business opportunities{" "}
            <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
              before they become obvious.
            </span>
          </h1>

          {/* Value Proposition Subtitle */}
          <p className="text-xs sm:text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium px-1">
            BizNearYou tracks public growth signals, official careers pages, and local commercial demand to help job seekers, recruiters, and sales pros discover unadvertised opportunities near them.
          </p>

          {/* Main Search Bar Form */}
          <div className="space-y-3 max-w-3xl mx-auto w-full">
            <form onSubmit={handleSearchSubmit} className="bg-slate-50 dark:bg-slate-800 p-2 sm:p-2.5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md flex flex-col sm:flex-row gap-2 w-full">
              <div className="flex-1 flex items-center gap-2 bg-white dark:bg-slate-900 px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 min-w-0">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Business category, skill, or role (e.g. Dental, Plumbing)..."
                  className="w-full bg-transparent text-xs sm:text-sm font-semibold focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 min-w-0"
                />
              </div>

              <div className="flex-1 flex items-center gap-2 bg-white dark:bg-slate-900 px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 min-w-0">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  placeholder="City or ZIP code (e.g. Austin, TX)..."
                  className="w-full bg-transparent text-xs sm:text-sm font-semibold focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 min-w-0"
                />
              </div>

              <button
                type="submit"
                className="w-full sm:w-auto min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-6 py-2.5 rounded-xl shadow-md transition cursor-pointer text-xs sm:text-sm flex items-center justify-center gap-2 shrink-0"
              >
                <span>Find Businesses</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            {/* Suggested searches chips */}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5 pt-1 px-1">
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider shrink-0 mr-1">Popular:</span>
              {[
                { name: "Medical Clinics", slug: "medical-healthcare" },
                { name: "Dental Clinics", slug: "dental-clinics" },
                { name: "Plumbing & Trades", slug: "plumbing-trades" },
                { name: "Legal & Accounting", slug: "legal-accounting" },
                { name: "Tech Agencies", slug: "tech-agencies" },
                { name: "Retail & Hospitality", slug: "retail-hospitality" }
              ].map((pill) => {
                const resLoc = locationQuery ? resolveLocationSlug(locationQuery) : null;
                const locSlug = resLoc && resLoc.type !== "invalid" && resLoc.type !== "reserved" ? resLoc.normalizedSlug : "";
                const targetUrl = locSlug ? `/jobs/${locSlug}/${pill.slug}` : `/jobs/${pill.slug}`;

                return (
                  <a
                    key={pill.slug}
                    href={targetUrl}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(targetUrl);
                    }}
                    className="px-2.5 py-1 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 hover:bg-blue-50/80 dark:hover:bg-slate-700 hover:border-blue-300 dark:hover:border-slate-600 hover:text-blue-700 dark:hover:text-blue-300 text-[11px] font-bold transition cursor-pointer border border-slate-200/90 dark:border-slate-700 shadow-2xs flex items-center gap-1 group"
                  >
                    <span className="text-blue-600 dark:text-blue-400 font-extrabold group-hover:scale-110 transition-transform">+</span>
                    <span>{pill.name}</span>
                  </a>
                );
              })}
            </div>
          </div>

          {/* 5-Second Clarity Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-6 pt-2 text-xs font-extrabold text-slate-600 dark:text-slate-300">
            <div className="flex items-center gap-1.5 bg-white dark:bg-slate-800/80 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>Unposted Local Businesses</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white dark:bg-slate-800/80 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700">
              <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              <span>Evidence-Based Signal Tracking</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white dark:bg-slate-800/80 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>Conservative Evidence Standard</span>
            </div>
          </div>

        </div>
      </section>

      {/* 2. VISUAL PRODUCT RESULT CARD DEMO PREVIEW */}
      <section className="py-12 sm:py-16 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 text-left">
          
          <div className="text-center max-w-3xl mx-auto space-y-2.5">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/80 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-xs font-bold uppercase tracking-wider">
              <Eye className="w-3.5 h-3.5" />
              <span>Interactive Result Preview</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              See how BizNearYou presents business intelligence
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
              Toggle audience modes and signal levels below to preview how our card layout adapts for different user needs:
            </p>
          </div>

          {/* Interactive Controls Bar */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-6 shadow-xs space-y-5">
            
            {/* Audience Mode Selector */}
            <div className="space-y-2">
              <label className="text-xs font-black uppercase text-slate-500 dark:text-slate-400 tracking-wider block">
                Select Your Role / Audience Mode:
              </label>
              <div className="grid grid-cols-2 min-[480px]:grid-cols-3 sm:flex sm:flex-wrap gap-2">
                {[
                  { id: "job_seeker", label: "Job Seeker" },
                  { id: "recruiter", label: "Recruiter" },
                  { id: "sales_professional", label: "Sales & B2B" },
                  { id: "agency_freelancer", label: "Agency & Freelancer" },
                  { id: "business_explorer", label: "Local Explorer" }
                ].map(aud => (
                  <button
                    key={aud.id}
                    onClick={() => setActiveAudienceMode(aud.id as AudienceModeId)}
                    className={`min-h-[42px] px-3 py-2 rounded-xl text-xs font-extrabold transition cursor-pointer border flex items-center justify-center text-center ${
                      activeAudienceMode === aud.id
                        ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                        : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    {aud.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Signal Level Selector Tabs */}
            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <label className="text-xs font-black uppercase text-slate-500 dark:text-slate-400 tracking-wider block">
                Select Signal Confidence Tier:
              </label>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {(["high", "possible", "unconfirmed", "improbable", "low"] as const).map(sig => (
                  <button
                    key={sig}
                    onClick={() => setDemoSignalTab(sig)}
                    className={`min-h-[38px] px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer border ${
                      demoSignalTab === sig
                        ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 border-slate-900 dark:border-slate-100"
                        : "bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:text-slate-900"
                    }`}
                  >
                    {sig.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* MOCK RESULT CARD */}
            <div className="mt-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-6 shadow-sm space-y-4 text-left relative">
              
              {/* Card Header */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-extrabold uppercase tracking-wider text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-800/80 px-2.5 py-0.5 rounded-md">
                      Healthcare & Dental
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 shrink-0" /> Austin, TX • 1.2 miles away
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white break-words">
                    Lakeside Family Dental Practice
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium break-words">
                    Verified Phone: (512) 555-0192 • Website: lakesidedentalaustin.com
                  </p>
                </div>

                {/* Signal Badge */}
                <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-1 shrink-0 pt-2 sm:pt-0 border-t sm:border-0 border-slate-200 dark:border-slate-800">
                  <span className={`px-3 py-1 rounded-lg text-xs font-black border ${currentDemoSignal.color}`}>
                    {currentDemoSignal.badge}
                  </span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                    {currentDemoSignal.freshness}
                  </span>
                </div>
              </div>

              {/* Evidence & Reason Box */}
              <div className="bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 rounded-xl p-3 sm:p-3.5 text-xs space-y-1">
                <p className="font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
                  Signal Evidence & Observations:
                </p>
                <p className="text-slate-700 dark:text-slate-200 leading-relaxed font-medium">
                  {currentDemoSignal.reason}
                </p>
              </div>

              {/* Card Footer Actions (Adapted by Audience Mode) */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                <div className="text-xs font-extrabold text-slate-700 dark:text-slate-300">
                  {activeAudienceMode === "job_seeker" && "Recommended Action: Contact Practice Manager directly"}
                  {activeAudienceMode === "recruiter" && "Recommended Action: Add account to recruiting pipeline"}
                  {activeAudienceMode === "sales_professional" && "Recommended Action: Pitch commercial tech/web solutions"}
                  {activeAudienceMode === "agency_freelancer" && "Recommended Action: Propose website & SEO performance audit"}
                  {activeAudienceMode === "business_explorer" && "Recommended Action: Review business footprint & address"}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => onNavigate("/how-signals-work")}
                    className="flex-1 sm:flex-none min-h-[40px] bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 px-3.5 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center text-center"
                  >
                    View Evidence
                  </button>
                  <button
                    onClick={() => onLogin(true)}
                    className="flex-1 sm:flex-none min-h-[40px] bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-extrabold transition cursor-pointer flex items-center justify-center gap-1.5 shadow-xs"
                  >
                    <span>
                      {activeAudienceMode === "job_seeker" && "Contact Employer"}
                      {activeAudienceMode === "recruiter" && "Add to CRM"}
                      {activeAudienceMode === "sales_professional" && "Save Lead"}
                      {activeAudienceMode === "agency_freelancer" && "Audit Prospect"}
                      {activeAudienceMode === "business_explorer" && "Save Business"}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* 3. WHY THIS EXISTS & DIFFERENT FROM JOB BOARDS */}
      <section className="py-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-12 text-left">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Why BizNearYou exists & what makes it different
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
              Traditional job boards are designed for large corporations with huge budgets. Here is why proactive direct sourcing wins for local businesses:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Traditional Job Boards */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 dark:bg-rose-950/80 border border-rose-200 dark:border-rose-800/80 text-rose-700 dark:text-rose-300 text-xs font-extrabold uppercase">
                <X className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                <span>Traditional Job Boards</span>
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white">
                Expensive, Overcrowded, & Passive
              </h3>
              <ul className="space-y-3 text-xs text-slate-700 dark:text-slate-300 font-medium">
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 dark:text-rose-400 font-black">✗</span>
                  <span><strong>High posting fees:</strong> Costs $300 to $1,000+ per month per ad, pricing out small local clinics and trade shops.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 dark:text-rose-400 font-black">✗</span>
                  <span><strong>500+ applicant floods:</strong> Automated bots fill job queues with unqualified non-local applicants.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 dark:text-rose-400 font-black">✗</span>
                  <span><strong>Phantom job postings:</strong> Expired job ads remain online for months to collect resumes.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 dark:text-rose-400 font-black">✗</span>
                  <span><strong>ATS rejection bots:</strong> Automated software filters out qualified candidates based on missing keywords.</span>
                </li>
              </ul>
            </div>

            {/* BizNearYou Direct Signal Sourcing */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/80 border border-emerald-200 dark:border-emerald-800/80 text-emerald-700 dark:text-emerald-300 text-xs font-extrabold uppercase">
                <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>BizNearYou Signal Intelligence</span>
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white">
                Direct, Evidence-Based, & Proactive
              </h3>
              <ul className="space-y-3 text-xs text-slate-700 dark:text-slate-300 font-medium">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 font-black">✓</span>
                  <span><strong>Public contact routes:</strong> Access official phone numbers, domain websites, and careers intake pages.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 font-black">✓</span>
                  <span><strong>Local business coverage:</strong> Search physical commercial employers beyond standard job board listings.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 font-black">✓</span>
                  <span><strong>Conservative evidence labels:</strong> Clear High, Possible, and Unconfirmed labels with freshness indicators.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 font-black">✓</span>
                  <span><strong>Kanban outreach tracking:</strong> Organize contacts, follow-ups, and notes in your personal CRM.</span>
                </li>
              </ul>
            </div>

          </div>

        </div>
      </section>

      {/* 4. AUDIENCE VALUE CARDS (10 Key Roles) */}
      <section className="py-16 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-10 text-left">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Tailored value for every professional
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
              Explore how BizNearYou solves specific challenges for 10 distinct audience modes:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audienceCards.map((aud) => (
              <div
                key={aud.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 space-y-4 flex flex-col justify-between hover:border-blue-500 transition shadow-xs group"
              >
                <div className="space-y-3">
                  <span className="text-[10px] font-black uppercase tracking-wider text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/80 px-2.5 py-1 rounded">
                    {aud.title}
                  </span>
                  
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-rose-600 dark:text-rose-400">
                      Problem: {aud.painPoint}
                    </p>
                    <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-medium pt-1">
                      {aud.benefit}
                    </p>
                  </div>

                  <div className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl text-[11px] text-slate-600 dark:text-slate-400 font-mono">
                    <span className="font-bold text-slate-900 dark:text-slate-200">Example Signal: </span>
                    {aud.exampleSignal}
                  </div>
                </div>

                <button
                  onClick={() => onNavigate(aud.link)}
                  className="text-xs font-black text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1 cursor-pointer pt-2 group-hover:translate-x-1 transition-transform"
                >
                  <span>Explore for {aud.title.split(" ")[0]}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 5. HOW IT WORKS (3-Step Direct Process) */}
      <section className="py-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-12 text-center">
          
          <div className="max-w-3xl mx-auto space-y-3">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              How direct outreach works in 3 simple steps
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
              BizNearYou equips you with map-driven target discovery, evidence verification, and structured outreach tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            
            {/* Step 1 */}
            <div className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 text-white font-black text-sm flex items-center justify-center">
                01
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white">
                Discover Nearby Targets
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
                Enter your city or ZIP to view nearby operating businesses across healthcare, trades, technology, retail, and professional services.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white font-black text-sm flex items-center justify-center">
                02
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white">
                Inspect Public Evidence
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
                Review verified signal badges (High, Possible, Unconfirmed), source URLs, and freshness timestamps to gauge operational growth.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white font-black text-sm flex items-center justify-center">
                03
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white">
                Initiate Direct Contact
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
                Use verified contact details to send polite introductions, and log your progress in your personal CRM.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* 6. COMPARISON TABLE */}
      <section className="py-12 sm:py-16 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 text-left">
          
          <div className="text-center max-w-3xl mx-auto space-y-2.5">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Platform Feature Comparison
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
              General product focus compared across local search tools, job boards, map directories, and spreadsheets:
            </p>
          </div>

          {/* Desktop & Tablet Table */}
          <div className="hidden sm:block overflow-x-auto bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
            <table className="w-full text-left text-xs min-w-[540px]">
              <thead className="bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 font-extrabold uppercase text-[10px]">
                <tr>
                  <th className="p-3.5 sm:p-4">Capability / Focus</th>
                  <th className="p-3.5 sm:p-4 text-blue-600 dark:text-blue-400">BizNearYou</th>
                  <th className="p-3.5 sm:p-4 text-slate-500">Major Job Boards</th>
                  <th className="p-3.5 sm:p-4 text-slate-500">General Map Directories</th>
                  <th className="p-3.5 sm:p-4 text-slate-500">Spreadsheets</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                <tr>
                  <td className="p-3.5 sm:p-4 font-bold text-slate-900 dark:text-white">Search Nearby Businesses</td>
                  <td className="p-3.5 sm:p-4 text-emerald-600 dark:text-emerald-400 font-black">✓ Physical Local Employers</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Limited / Indirect</td>
                  <td className="p-3.5 sm:p-4 text-slate-700 dark:text-slate-300">✓ Yes (Commercial Places)</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Manual Entry</td>
                </tr>
                <tr>
                  <td className="p-3.5 sm:p-4 font-bold text-slate-900 dark:text-white">Save & Organize Employers</td>
                  <td className="p-3.5 sm:p-4 text-emerald-600 dark:text-emerald-400 font-black">✓ Saved Lists & Notes</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Save Job Listings</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Save Saved Places</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Manual Setup</td>
                </tr>
                <tr>
                  <td className="p-3.5 sm:p-4 font-bold text-slate-900 dark:text-white">Audience Business Signals</td>
                  <td className="p-3.5 sm:p-4 text-emerald-600 dark:text-emerald-400 font-black">✓ Public Growth Indicators</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Posted Ads Only</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Customer Review Focus</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">None</td>
                </tr>
                <tr>
                  <td className="p-3.5 sm:p-4 font-bold text-slate-900 dark:text-white">Public Source & Contact Links</td>
                  <td className="p-3.5 sm:p-4 text-emerald-600 dark:text-emerald-400 font-black">✓ Phone, Site & Careers Link</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Internal ATS Forms</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Website & Phone</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Manual Search</td>
                </tr>
                <tr>
                  <td className="p-3.5 sm:p-4 font-bold text-slate-900 dark:text-white">Outreach Organization</td>
                  <td className="p-3.5 sm:p-4 text-emerald-600 dark:text-emerald-400 font-black">✓ Built-in Kanban Board</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Application History</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">None</td>
                  <td className="p-3.5 sm:p-4 text-slate-500">Manual Rows</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Stacked Cards View */}
          <div className="block sm:hidden space-y-4">
            {[
              {
                title: "Search Nearby Businesses",
                bizNearYou: "Physical Local Employers",
                jobBoards: "Limited / Indirect",
                maps: "Yes (Commercial Places)",
                spreadsheets: "Manual Entry"
              },
              {
                title: "Save & Organize Employers",
                bizNearYou: "Saved Lists & Notes",
                jobBoards: "Save Job Listings",
                maps: "Saved Places Only",
                spreadsheets: "Manual Setup"
              },
              {
                title: "Audience Business Signals",
                bizNearYou: "Public Growth Indicators",
                jobBoards: "Posted Ads Only",
                maps: "Customer Reviews",
                spreadsheets: "None"
              },
              {
                title: "Public Source & Contact Links",
                bizNearYou: "Phone, Site & Careers Link",
                jobBoards: "Internal ATS Forms",
                maps: "Website & Phone",
                spreadsheets: "Manual Search"
              },
              {
                title: "Outreach Organization",
                bizNearYou: "Built-in Kanban Board",
                jobBoards: "Application History",
                maps: "None",
                spreadsheets: "Manual Rows"
              }
            ].map((card, idx) => (
              <div key={idx} className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 space-y-2 text-xs">
                <div className="font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-1.5 text-sm">
                  {card.title}
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <span className="text-[10px] text-blue-600 dark:text-blue-400 font-extrabold block">BizNearYou</span>
                    <span className="font-bold text-slate-900 dark:text-white">{card.bizNearYou}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 font-extrabold block">Job Boards</span>
                    <span className="text-slate-600 dark:text-slate-400">{card.jobBoards}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 font-extrabold block">Map Directories</span>
                    <span className="text-slate-600 dark:text-slate-400">{card.maps}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 font-extrabold block">Spreadsheets</span>
                    <span className="text-slate-600 dark:text-slate-400">{card.spreadsheets}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-slate-500 dark:text-slate-400 italic text-center pt-2">
            Features vary by platform and may change. This comparison describes general product focus, not every feature offered by every service.
          </p>

        </div>
      </section>

      {/* 7. POPULAR SEARCH INTENT LINKS GRID */}
      <section className="py-12 sm:py-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 text-left">
          
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Popular ways to search & explore
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
              Explore dedicated solution pages tailored for specific search intents:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 text-xs font-bold">
            {[
              { label: "Businesses Hiring Near Me", path: "/businesses-hiring-near-me" },
              { label: "Hidden Jobs Near Me", path: "/hidden-jobs" },
              { label: "Companies Hiring Without Ads", path: "/companies-hiring-without-job-postings" },
              { label: "Small Businesses Hiring", path: "/small-businesses-hiring-near-me" },
              { label: "Local Job Search Tool", path: "/local-job-search-tool" },
              { label: "Recruiter Business Search", path: "/recruiter-business-search" },
              { label: "Local Lead Finder", path: "/local-lead-finder" },
              { label: "Agency Client Finder", path: "/agency-client-finder" },
              { label: "How Signals Work", path: "/how-signals-work" },
              { label: "Vs. Job Boards", path: "/compare/biznearyou-vs-job-boards" },
              { label: "Vs. Google Maps", path: "/compare/biznearyou-vs-google-maps" },
              { label: "Vs. Spreadsheets", path: "/compare/biznearyou-vs-spreadsheets" }
            ].map((linkItem, idx) => (
              <button
                key={idx}
                onClick={() => onNavigate(linkItem.path)}
                className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition cursor-pointer text-left flex items-center justify-between min-h-[44px] group"
              >
                <span>{linkItem.label}</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-1 transition-transform shrink-0" />
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* 8. PRICING / UPGRADE CTA BLOCK */}
      <section className="py-12 sm:py-16 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-3xl p-6 sm:p-12 shadow-sm text-center space-y-5 border border-slate-200 dark:border-slate-800">
          <p className="text-[11px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
            START EXPLORING
          </p>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight leading-tight text-slate-900 dark:text-white">
            Start finding unadvertised local opportunities today
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm max-w-2xl mx-auto leading-relaxed">
            Search nearby businesses, review available public signals, save promising results, and organize your next steps.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
            <button
              onClick={() => onNavigate("/find-jobs")}
              className="w-full sm:w-auto min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-6 py-3 rounded-xl shadow-xs transition cursor-pointer text-xs sm:text-sm flex items-center justify-center gap-2"
            >
              <span>Start Free Local Search</span>
              <ArrowRight className="w-4 h-4 shrink-0" />
            </button>
            <button
              onClick={() => onNavigate("/pricing")}
              className="w-full sm:w-auto min-h-[44px] bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-white border border-slate-300 dark:border-slate-700 font-extrabold px-6 py-3 rounded-xl shadow-xs transition cursor-pointer text-xs sm:text-sm flex items-center justify-center"
            >
              View Subscription Plans
            </button>
          </div>
        </div>
      </section>

      {/* 9. FREQUENTLY ASKED QUESTIONS (FAQ Accordion) */}
      <section className="py-16 bg-white dark:bg-slate-900 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8 text-left">
          
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">
              Clear answers about our data sources, hiring signals, and conservative evidence standards:
            </p>
          </div>

          <div className="space-y-3">
            {faqList.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div
                  key={idx}
                  className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden bg-slate-50 dark:bg-slate-800/50 transition"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    className="w-full p-5 text-left font-extrabold text-sm text-slate-900 dark:text-white flex items-center justify-between cursor-pointer hover:text-blue-600 transition"
                  >
                    <span>{faq.q}</span>
                    <ChevronRight className={`w-4 h-4 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-90 text-blue-600" : "text-slate-400"}`} />
                  </button>
                  {isOpen && (
                    <div className="p-5 pt-0 text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium border-t border-slate-200/60 dark:border-slate-700/60">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </section>

    </div>
  );
}
